import { formatSettingValue, type ResolvedSetting, settingLabelKey } from '@lib/customization/appSettings';
import { useLocalization } from '@lib/localization';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  pendingDotStyles, settingRowChangedStyles, settingRowDormantStyles, settingRowPendingStyles, settingRowStyles,
} from '@pages/AppSettings.styles';
import { type AppSettingDefinition, type AppSettingValue } from '@store/api/appSettingsEndpoints';
import { InfoCircleIcon, UndoAltIcon } from 'react-line-awesome';
import SettingControl from './SettingControl';

interface Props {
  definition: AppSettingDefinition;
  resolved: ResolvedSetting;
  /** The value shown is a change that has not been saved yet. */
  pending: boolean;
  disabled: boolean;
  /** Set while the setting has no effect, naming what would give it one. */
  dormantHint?: string;
  lineColor?: string;
  onChange: (value: AppSettingValue) => void;
  onRevert: () => void;
}

const dotStyles = { width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main', flexShrink: 0 };

/**
 * One setting: what it is, where its value comes from, and the control that changes it.
 *
 * The control always shows the value that applies here — set here, the organization's beneath
 * a team, or the app's default — and the row around it says which. Changing it sets it here;
 * reverting, or choosing again the value it would fall back to, takes it off. All of it as
 * the page's draft holds it: a row whose change is not saved yet is marked as such.
 */
const SettingRow: React.FC<Props> = ({
  definition, resolved, pending, disabled, dormantHint, lineColor, onChange, onRevert,
}) => {
  const { t, currentLanguage } = useLocalization();

  const isSetHere = resolved.source === 'own';
  const fallsBackToOrganization = resolved.fallbackSource === 'organization';
  const fallbackText = formatSettingValue(definition, resolved.fallback, t, currentLanguage);

  const origin = isSetHere
    ? (
      <>
        <Box sx={dotStyles} />
        <Box component="span" sx={{ color: 'primary.light', fontWeight: 600 }}>{t('AppSettings_SetHere')}</Box>
        <Box component="span">·</Box>
        <Box component="span">
          {t(fallsBackToOrganization ? 'AppSettings_OrganizationValue' : 'AppSettings_DefaultValue', [fallbackText])}
        </Box>
      </>
    )
    : <Box component="span">{t(fallsBackToOrganization ? 'AppSettings_FromOrganization' : 'AppSettings_AppDefault')}</Box>;

  return (
    <Stack
      direction="row"
      sx={[
        settingRowStyles, isSetHere && settingRowChangedStyles, pending && settingRowPendingStyles,
        Boolean(dormantHint) && settingRowDormantStyles,
      ]}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography className="setting-label" sx={{ fontWeight: 600, fontSize: 14.5 }} noWrap>
          {t(settingLabelKey(definition.key))}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ mt: 0.25, fontSize: 12.5, color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.25 }}
        >
          {pending && (
            <Tooltip title={t('Customization_Designer_Unsaved')} arrow>
              <Box sx={pendingDotStyles} />
            </Tooltip>
          )}
          {origin}
          {dormantHint && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ '& i': { fontSize: 14 } }}>
              <Box component="span">·</Box>
              <InfoCircleIcon />
              <Box component="span">{dormantHint}</Box>
            </Stack>
          )}
        </Stack>
      </Box>

      <Box className="setting-control" sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <SettingControl
          definition={definition}
          value={resolved.value}
          disabled={disabled}
          lineColor={lineColor}
          onChange={onChange}
        />
      </Box>

      {/* Held open when empty, so the controls down the list keep one right edge. */}
      <Box sx={{ width: 34, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        {isSetHere && !disabled && (
          <Tooltip title={t(fallsBackToOrganization ? 'AppSettings_Revert_Organization' : 'AppSettings_Revert', [fallbackText])} arrow>
            <IconButton size="small" onClick={onRevert} sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
              <UndoAltIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};

export default SettingRow;
